import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SidebarItem, SidebarSection } from '@app/app/shared/models/sidebar/sidebar.model';
import { LucideChevronDown, LucidePlus } from '@lucide/angular';
import { Subject, filter, takeUntil } from 'rxjs';

@Component({
  selector: 'ptl-admin-sidebar-section',
  templateUrl: './admin-sidebar-section.component.html',
  styleUrls: ['./admin-sidebar-section.component.scss'],
})
export class AdminSidebarSectionComponent implements OnInit, OnDestroy {
  @Input() section: SidebarSection;
  @Output() addContent = new EventEmitter<void>();
  @Output() itemClick = new EventEmitter<{ item: SidebarItem; event: Event }>();

  protected readonly LucideChevronDown = LucideChevronDown;
  protected readonly LucidePlus = LucidePlus;

  expanded = false;

  private static readonly opened$ = new Subject<AdminSidebarSectionComponent>();
  private readonly destroy$ = new Subject<void>();
  private lastUrl: string | undefined;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    AdminSidebarSectionComponent.opened$.pipe(takeUntil(this.destroy$)).subscribe((opener) => {
      if (opener !== this) this.expanded = false;
    });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.syncIfUrlChanged());

    this.syncIfUrlChanged();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  onItemClick(event: Event, item: SidebarItem): void {
    this.expanded = true;
    AdminSidebarSectionComponent.opened$.next(this);
    this.itemClick.emit({ item, event });
  }

  toggleItemExpand(event: Event, item: SidebarItem): void {
    event.preventDefault();
    event.stopPropagation();
    item.expanded = !item.expanded;
  }

  onAddContent(event: Event): void {
    event.stopPropagation();
    this.addContent.emit();
  }

  isRouteActive(route: string, exact = false): boolean {
    const url = this.router.serializeUrl(this.router.createUrlTree([route], { relativeTo: this.route }));
    return this.router.isActive(url, {
      paths: exact ? 'exact' : 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  isItemOrChildActive(item: SidebarItem): boolean {
    const route = item.url || item.uri;
    if (route && this.isRouteActive(route)) return true;
    return item.children?.some((c) => this.isItemOrChildActive(c)) ?? false;
  }

  isSectionActive(): boolean {
    const root = this.section.rootNavItem?.url || this.section.rootNavItem?.uri;
    if (root && this.isRouteActive(root, true)) return true;
    return this.section.items.some((i) => this.isItemOrChildActive(i));
  }

  private syncIfUrlChanged(): void {
    if (this.router.url === this.lastUrl) return;
    this.lastUrl = this.router.url;
    this.expanded = this.isSectionActive();
    if (this.expanded) AdminSidebarSectionComponent.opened$.next(this);
  }
}
